import { Box, Checkbox, ListItem, ListItemIcon, Stack, TextField, Typography } from '@mui/material'
import { usePlanningStore } from '@planning/AppProvider'
import { UnitLabelType } from '@planning/app/api'
import { YesNoChipBox } from '@planning/components/dangerous-goods/YesNoChipBox'
import { ImdgCodes } from '@planning/constants'
import { useTranslate } from '@tolgee/react'
import { FieldBox, MobileViewAutocomplete, useMinimalsTheme } from '@tom-ui/ui'
import { IFormStore, useRegisterSubFormStore } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { UnitLabelSubFormStore } from './UnitLabelSubFormStore'
import { UnitLabelFormData } from './unit-label-helper'

export type UnitLabelSubFormProps<T> = {
  formStore: IFormStore<T>
  name: keyof T
  initialValue?: UnitLabelFormData
  onChange?: (value: UnitLabelFormData) => void
}

export const UnitLabelSubForm = observer(
  <T,>({ formStore, name, initialValue, onChange }: UnitLabelSubFormProps<T>) => {
    const theme = useMinimalsTheme()
    const { t } = useTranslate()
    const { dangerousGoodsDefinitionDataStore } = usePlanningStore()
    const [imoClassInput, setImoClassInput] = useState('')

    useEffect(() => {
      dangerousGoodsDefinitionDataStore.setPageSize(100000)
    }, [dangerousGoodsDefinitionDataStore])

    const store = useRegisterSubFormStore(formStore, name, new UnitLabelSubFormStore())

    useEffect(() => {
      store.setValues(initialValue)
    }, [initialValue, store])

    const handleChange = () => {
      if (onChange) {
        onChange(store.data)
      }
    }

    const handleIsUnitLabelChange = (value: boolean) => {
      store.isUnitLabel.setValue(value)
      handleChange()
    }

    const handleImoClassKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault()

        const matchingOption = ImdgCodes.find(option =>
          option.toLowerCase().includes(imoClassInput),
        )

        if (matchingOption) {
          if (!store.imoClasses.data.includes(matchingOption))
            store.imoClasses.setValue([...store.imoClasses.data, matchingOption])
          else store.imoClasses.setValue(store.imoClasses.data)
        }
      }
    }

    return (
      <FieldBox
        label={t('labels', 'Labels')}
        hasDivider={store.isUnitLabelSelected}
        rightRender={
          <YesNoChipBox
            activeOption={store.isUnitLabelSelected ? 'Yes' : 'No'}
            dataCyYes='unitLabel-field-yes'
            onClickYes={() => handleIsUnitLabelChange(true)}
            onClickNo={() => {
              handleIsUnitLabelChange(false)
            }}
          />
        }
      >
        {store.isUnitLabelSelected && (
          <Stack spacing={theme.customSpacing.m}>
            <MobileViewAutocomplete
              {...store.imoClasses.props}
              multiple
              disableCloseOnSelect
              fullWidth
              options={ImdgCodes}
              onChange={(_, assignedCodes) => {
                store.imoClasses.setValue(assignedCodes)
                handleChange()
              }}
              onInputChange={(_, value) => setImoClassInput(value)}
              renderOption={(props, value) =>
                CustomizedListItem(props, value, store.imoClasses.data)
              }
              renderInput={params => (
                <TextField
                  {...params}
                  required
                  sx={{ marginTop: '1rem' }}
                  onKeyDown={handleImoClassKeyDown}
                  label={`${t('imoClass', 'IMO Class')}`}
                  error={!!store.imoClasses.error}
                  helperText={
                    store.imoClasses.error ? (
                      t('fieldIsRequired', 'Field is required.')
                    ) : (
                      <Typography variant='caption'>
                        {t(
                          'iMOIsUsuallyAOneOrTwoDigitFoundOnTheLabel',
                          'IMO is usually a one or two digit found on the label.',
                        )}
                      </Typography>
                    )
                  }
                />
              )}
              data-cy='unitLabel-imoClasses-input'
              headerOffset={theme.spacing(10)}
            />

            <MobileViewAutocomplete
              headerOffset={theme.spacing(10)}
              {...store.unNumbers.props}
              multiple
              disableCloseOnSelect
              fullWidth
              options={dangerousGoodsDefinitionDataStore.pageItems
                .map(i => i.unNumber)
                .filter(i => i !== null && i !== undefined)}
              onChange={(_, unNumbers) => {
                store.unNumbers.setValue(unNumbers as any)
                handleChange()
              }}
              renderOption={(props, value) =>
                CustomizedListItem(props, value as string, store.unNumbers.data)
              }
              renderInput={params => (
                <TextField
                  {...params}
                  sx={{ marginTop: '1rem' }}
                  label={t('unNumbers', 'UN numbers')}
                  helperText={
                    <Typography variant='caption'>
                      {t(
                        'uNNumberIsUsuallyAFourDigitCodeLabelIfNotFoundInTheListAddANewOneByPressingENTER',
                        'UN number is usually a 4 digit code. If not found in the list, add a new one by pressing ENTER.',
                      )}
                    </Typography>
                  }
                />
              )}
              data-cy='unitLabel-unNumbers-input'
              freeSolo
            />

            <MobileViewAutocomplete
              headerOffset={theme.spacing(10)}
              {...store.types.props}
              multiple
              disableCloseOnSelect
              fullWidth
              options={Object.values(UnitLabelType)}
              getOptionLabel={option => t(option)}
              onChange={(_, types) => {
                store.types.setValue(types)
                handleChange()
              }}
              renderOption={(props, value) =>
                CustomizedListItem(props, value, store.types.data, true)
              }
              renderInput={params => (
                <TextField {...params} sx={{ marginTop: '1rem' }} label={t('label', 'Label')} />
              )}
              data-cy='unitLabel-types-input'
            />
          </Stack>
        )}
      </FieldBox>
    )

    function CustomizedListItem(
      props: React.HTMLAttributes<HTMLLIElement>,
      value: string,
      selectedValues: string[],
      useTranslation = false,
    ) {
      return (
        <ListItem {...props}>
          <ListItemIcon>
            <Checkbox edge='start' checked={selectedValues.includes(value)} disableRipple />
          </ListItemIcon>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant='body2'>{useTranslation ? t(value) : value}</Typography>
          </Box>
        </ListItem>
      )
    }
  },
)
